// Références de chantiers ETT (spec § 10) : la preuve que l'assistant peut citer dans un premier message. Liste
// commune, lue par tous, tenue par les administrateurs du client (la base refuse l'écriture aux autres). Tant
// qu'elle est vide, l'écran du message le signale : sans référence, le message ne cite aucune preuve.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, Plus, Trash2 } from "lucide-react";
import { useGetIdentity, useGetOne, useNotify, useTranslate } from "ra-core";
import { type FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";

type SellerReference = {
  id: number;
  title: string;
  structure_type: string | null;
  country: string | null;
  replaced: string | null;
  result: string | null;
  year: number | null;
  source_url: string | null;
};

const FIELDS = [
  "title",
  "structure_type",
  "country",
  "year",
  "replaced",
  "result",
  "source_url",
] as const;
type Field = (typeof FIELDS)[number];

const useSellerReferences = () =>
  useQuery({
    queryKey: ["seller_references"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("seller_references")
        .select(
          "id, title, structure_type, country, replaced, result, year, source_url",
        )
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as SellerReference[];
    },
  });

/** Administrateur de l'équipe du client : seul à tenir la liste. */
const useClientAdmin = () => {
  const { identity } = useGetIdentity();
  const { data: sale } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? 0 },
    { enabled: !!identity?.id },
  );
  return !!sale?.administrator && !sale.partner;
};

/** À côté du message : alerte tant que la liste est vide, sinon un lien discret vers elle. */
export const SellerReferencesNotice = () => {
  const translate = useTranslate();
  const admin = useClientAdmin();
  const { data: references, isPending } = useSellerReferences();
  const [open, setOpen] = useState(false);
  if (isPending) return null;
  const empty = !references?.length;

  return (
    <>
      {empty ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <Info className="h-4 w-4 shrink-0" />
          <p className="flex-1">
            {translate(
              `resources.projects.references.${admin ? "empty" : "empty_ask"}`,
            )}
          </p>
          {admin && (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
              <Plus />
              {translate("resources.projects.references.add")}
            </Button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="self-start text-xs text-muted-foreground underline cursor-pointer"
          onClick={() => setOpen(true)}
        >
          {translate("resources.projects.references.count", {
            smart_count: references.length,
          })}
        </button>
      )}
      <SellerReferencesDialog
        open={open}
        onOpenChange={setOpen}
        references={references ?? []}
        admin={admin}
      />
    </>
  );
};

const SellerReferencesDialog = ({
  open,
  onOpenChange,
  references,
  admin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  references: SellerReference[];
  admin: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Partial<Record<Field, string>>>({});
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["seller_references"] });

  const add = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const row = Object.fromEntries(
      FIELDS.map((field) => {
        const value = values[field]?.trim() || null;
        return [field, field === "year" && value ? Number(value) : value];
      }),
    );
    const { error } = await getSupabaseClient()
      .from("seller_references")
      .insert(row);
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    setValues({});
    await refresh();
  };

  const remove = async (id: number) => {
    const { error } = await getSupabaseClient()
      .from("seller_references")
      .delete()
      .eq("id", id);
    if (error) notify(error.message, { type: "error" });
    else await refresh();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {translate("resources.projects.references.title")}
          </DialogTitle>
          <DialogDescription>
            {translate("resources.projects.references.intro")}
          </DialogDescription>
        </DialogHeader>
        {references.length > 0 && (
          <ul className="flex flex-col gap-2 text-sm">
            {references.map((reference) => (
              <li
                key={reference.id}
                className="flex items-start justify-between gap-2 rounded-md border p-2"
              >
                <div className="min-w-0">
                  <div className="font-medium">{reference.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {[
                      reference.structure_type,
                      reference.country,
                      reference.year,
                      reference.replaced,
                      reference.result,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                {admin && (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={translate(
                      "resources.projects.references.remove",
                    )}
                    onClick={() => remove(reference.id)}
                  >
                    <Trash2 />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {admin ? (
          <form onSubmit={add} className="flex flex-col gap-3">
            {FIELDS.map((field) => (
              <div key={field} className="flex flex-col gap-1">
                <Label htmlFor={`reference-${field}`}>
                  {translate(`resources.projects.references.fields.${field}`)}
                </Label>
                <Input
                  id={`reference-${field}`}
                  required={field === "title"}
                  type={
                    field === "year"
                      ? "number"
                      : field === "source_url"
                        ? "url"
                        : "text"
                  }
                  maxLength={field === "title" ? 200 : 300}
                  placeholder={translate(
                    `resources.projects.references.placeholders.${field}`,
                  )}
                  value={values[field] ?? ""}
                  onChange={(e) =>
                    setValues({ ...values, [field]: e.target.value })
                  }
                />
              </div>
            ))}
            <Button type="submit" disabled={busy} className="self-end">
              <Plus />
              {translate("resources.projects.references.add")}
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.references.read_only")}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
